import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  MousePointerClick,
  Share2,
  PenLine,
  Mail,
  BarChart3,
  Globe,
  ShoppingBag,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Service = {
  title: string;
  description: string;
  icon: LucideIcon;
  gradient: string;
};

const services: Service[] = [
  {
    title: "Search Engine Optimization",
    description: "Rank higher on Google with technical SEO, keyword strategy, and content optimization.",
    icon: Search,
    gradient: "dm-service-card__icon--orange",
  },
  {
    title: "Pay-Per-Click Advertising",
    description: "Drive instant traffic with targeted Google Ads, Meta Ads, and display campaigns.",
    icon: MousePointerClick,
    gradient: "dm-service-card__icon--rose",
  },
  {
    title: "Social Media Marketing",
    description: "Build brand awareness and engagement across Facebook, Instagram, LinkedIn, and more.",
    icon: Share2,
    gradient: "dm-service-card__icon--pink",
  },
  {
    title: "Content Marketing",
    description: "Attract and convert audiences with blogs, videos, infographics, and thought leadership.",
    icon: PenLine,
    gradient: "dm-service-card__icon--amber",
  },
  {
    title: "Email Marketing",
    description: "Nurture leads and boost retention with automated, personalized email campaigns.",
    icon: Mail,
    gradient: "dm-service-card__icon--violet",
  },
  {
    title: "Analytics & Reporting",
    description: "Track KPIs, measure ROI, and make data-backed decisions with transparent reporting.",
    icon: BarChart3,
    gradient: "dm-service-card__icon--blue",
  },
  {
    title: "Local SEO",
    description: "Dominate local search results and Google Maps for location-based businesses.",
    icon: Globe,
    gradient: "dm-service-card__icon--green",
  },
  {
    title: "E-Commerce Marketing",
    description: "Increase online store sales with product ads, retargeting, and conversion optimization.",
    icon: ShoppingBag,
    gradient: "dm-service-card__icon--indigo",
  },
];

const DmServices: React.FC = () => (
  <AnimatedSection as="section" className="dm-section dm-section--light" aria-labelledby="dm-services-title">
    <div className="dm-container">
      <SectionHeader
        eyebrow="Our Marketing Services"
        title="Full-Spectrum Digital Marketing Solutions"
        text="Every channel, every tactic — tailored to your business goals and budget."
        light
      />

      <div className="dm-service-grid">
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <motion.article
              key={service.title}
              className="dm-service-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              whileHover={{ y: -8, transition: { duration: 0.25 } }}
            >
              <div className={`dm-service-card__icon ${service.gradient}`}>
                <Icon size={24} aria-hidden="true" />
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </motion.article>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default DmServices;
